import React from 'react';
import type { ComposeAction, ComposeTab } from '../composeActions';
import { LAB_BENCH_DATA, type LabBench } from '../data/labData';
import { councilLabApiClient } from '../../../../services/councilLabApiClient';
import { councilApiClient } from '../../../../services/councilApiClient';

// 2026-05-17 belt-and-suspenders: after any lab action that mutates the
// proposal queue (inject / wipe), fire a window-level event so
// useCouncilProposals refreshes immediately. The WS path is the primary
// signal; this is the safety net Alan needed after lab injects hit the
// DB but the ProposalsChip stayed at 0 until F5 — a missed WS event
// (probably _broadcast_to_ws_impl racing with the council WS reconnect
// cycle we see in backend logs).
function notifyProposalsChanged(sessionId: string): void {
  window.dispatchEvent(
    new CustomEvent('council:proposals-changed', { detail: { sessionId } }),
  );
}

/**
 * Lab benches — 7 nested sub-tabs (Picker's Quiver / AI-DevOps Bench /
 * System Ops Bench / Storytelling Studio / Reflective Practice /
 * Compose Bench / Hypervisor Bench).
 *
 * LAB_BENCH_DATA is the verbatim copy of ComposeMenu.tsx::LAB_TABS,
 * imported from the new data/labData.ts module (per plan-triad blocker
 * #4 — eliminates the hand-transcription risk of populating LAB_BENCHES
 * inline). Transformation to ComposeTab[] happens at the render boundary
 * here, so the data shape and the discriminated-union shape evolve
 * independently.
 *
 * State for activeLabBenchId is OWNED BY ComposePanel root and passed
 * via props (NOT local useState) — frontend spec-triad: lazy-render
 * unmount of inactive top-level tabs would blow local state on every
 * top-tab toggle. Lifting it preserves the operator's bench position.
 */

// Transform LAB_BENCH_DATA's nested templates into ComposeAction[],
// dropping the Wave-2-only metadata fields (title, default_pack, baseline)
// that aren't surfaced in the user-facing render.
function benchToTab(bench: LabBench): ComposeTab {
  return {
    id: bench.id,
    emoji: bench.emoji,
    label: bench.label,
    descriptor: bench.descriptor,
    actions: bench.templates.map((t): ComposeAction => ({
      kind: 'insert',
      id: t.id ?? `${bench.id}-${t.label.slice(0, 16)}`,
      label: t.label,
      descriptor: t.descriptor,
      prompt: t.prompt,
    })),
  };
}

// v0.1 Item 8 — Hill Climbers bench. Hand-authored ComposeTab with
// exec-kind actions that call council-lab backend endpoints directly,
// so the operator can drive the propose_fork → review → approve loop
// from inside an active trail without a real peer LLM.
//
// Lives next to LAB_BENCH_DATA's data-derived benches as a manual
// append to LAB_BENCHES — keeps labData.ts pure data (no service
// imports) while still surfacing in the same Lab flyout.
const HILL_CLIMBERS_BENCH: ComposeTab = {
  id: 'hill-climbers',
  emoji: '🥾',
  label: 'Hill Climbers',
  descriptor: 'v0.1 substrate exercisers — drive propose_fork without a peer LLM',
  actions: [
    {
      kind: 'exec',
      id: 'HC-01',
      label: 'Inject 1 propose_fork',
      descriptor: 'Synthetic proposal from the first peer at their spine_root',
      run: async ({ sessionId, showToast }) => {
        if (!sessionId) {
          showToast('No session — start a chat first.', 'error');
          return;
        }
        const out = await councilLabApiClient.injectProposal(sessionId);
        notifyProposalsChanged(sessionId);
        showToast(`Injected ${out.kind} · ${out.id.slice(0, 8)}…`, 'success');
      },
    },
    {
      kind: 'exec',
      id: 'HC-02',
      label: 'Inject 5 propose_forks',
      descriptor: 'Bulk inject — exercises chip count + dialog scroll',
      run: async ({ sessionId, showToast }) => {
        if (!sessionId) {
          showToast('No session — start a chat first.', 'error');
          return;
        }
        const out = await councilLabApiClient.injectProposals(sessionId, 5);
        notifyProposalsChanged(sessionId);
        showToast(`Injected ${out.count} proposals`, 'success');
      },
    },
    {
      kind: 'exec',
      id: 'HC-03',
      label: 'Inject 10 propose_forks',
      descriptor: 'Stress the chip count + dialog scrolling',
      run: async ({ sessionId, showToast }) => {
        if (!sessionId) {
          showToast('No session — start a chat first.', 'error');
          return;
        }
        const out = await councilLabApiClient.injectProposals(sessionId, 10);
        notifyProposalsChanged(sessionId);
        showToast(`Injected ${out.count} proposals`, 'success');
      },
    },
    {
      kind: 'exec',
      id: 'HC-04',
      label: 'Wipe pending proposals',
      descriptor: 'DELETE pending rows · resolved rows preserved',
      run: async ({ sessionId, showToast }) => {
        if (!sessionId) {
          showToast('No session — start a chat first.', 'error');
          return;
        }
        const out = await councilLabApiClient.wipeProposals(sessionId);
        notifyProposalsChanged(sessionId);
        showToast(`Wiped ${out.wiped} pending proposal(s)`, 'info');
      },
    },
  ],
};

// v0.2 ChatDAG peer-spine refactor (post-cutover smoke bench, 2026-05-15).
// Six probes that each exercise one specific layout invariant of the new
// walker (topologyWalk + layoutGroups + layoutDag). Pair with the
// docs/superpowers/research/2026-05-15-chatdag-layout-test-scenarios.md
// matrix — each chip's id (TS-NN) cross-references a row in that doc.
const TRAIL_SMOKE_BENCH: ComposeTab = {
  id: 'trail-smoke',
  emoji: '🛤',
  label: 'Trail Smoke',
  descriptor: 'v0.2 walker probes — one layout invariant per chip',
  actions: [
    {
      kind: 'insert',
      id: 'TS-01',
      label: 'Bare round (no tools)',
      descriptor: 'Round → asst marker · ASST_PRELUDE_GAP (56) visible',
      prompt: 'Just say hi back, no tools.',
    },
    {
      kind: 'insert',
      id: 'TS-02',
      label: 'Multi-tool single round',
      descriptor: 'Stacks tool_calls right of round · lateral subtree clearance',
      prompt:
        'Without consulting peers, look up the active persona roster and then '
        + 'search the knowledge base for "documentary production schedule". '
        + 'Report what you find from each.',
    },
    {
      kind: 'insert',
      id: 'TS-03',
      label: 'consult_peers N=4 fan',
      descriptor: 'Peer fan = polar arc RIGHT of tool · NOT vertical stack',
      prompt:
        'Pull 4 peers from the storytelling pack and have them weigh in on '
        + 'whether a cold-open works for a 90-minute documentary about labor '
        + 'organizing. I want substantive disagreement.',
    },
    {
      kind: 'exec',
      id: 'TS-04',
      label: 'Inject 2 propose_forks',
      descriptor: 'After approve-both: N=2 sibling fan via polar arc downward',
      run: async ({ sessionId, showToast }) => {
        if (!sessionId) {
          showToast('No session — start a chat first.', 'error');
          return;
        }
        const out = await councilLabApiClient.injectProposals(sessionId, 2);
        notifyProposalsChanged(sessionId);
        showToast(`Injected ${out.count} proposals — approve both for N=2 fan test`, 'success');
      },
    },
    {
      kind: 'exec',
      id: 'TS-05',
      label: 'Inject 3 propose_forks',
      descriptor: 'After approve-all: N=3 spine_fan_chip auto-collapse · click expand',
      run: async ({ sessionId, showToast }) => {
        if (!sessionId) {
          showToast('No session — start a chat first.', 'error');
          return;
        }
        const out = await councilLabApiClient.injectProposals(sessionId, 3);
        notifyProposalsChanged(sessionId);
        showToast(`Injected ${out.count} proposals — approve all for spine_fan_chip test`, 'success');
      },
    },
    {
      kind: 'exec',
      id: 'TS-06',
      label: 'Wipe + Inject 6',
      descriptor: 'Clean restart — wipe pending then bulk-inject for fan-chip stress',
      run: async ({ sessionId, showToast }) => {
        if (!sessionId) {
          showToast('No session — start a chat first.', 'error');
          return;
        }
        const wipe = await councilLabApiClient.wipeProposals(sessionId);
        const inj = await councilLabApiClient.injectProposals(sessionId, 6);
        notifyProposalsChanged(sessionId);
        showToast(`Wiped ${wipe.wiped} · injected ${inj.count}`, 'success');
      },
    },
    {
      kind: 'exec',
      id: 'TS-07',
      label: 'Fire mock squad (N=3)',
      descriptor: 'Squad chrome E2E — banner + members + synth — $0 (mock_run)',
      run: async ({ sessionId, showToast }) => {
        if (!sessionId) {
          showToast('No session — start a chat first.', 'error');
          return;
        }
        try {
          const out = await councilLabApiClient.fireSquadMock(sessionId, 3);
          showToast(
            `Mock squad dispatched (${out.member_count} members, mock_run=true)`,
            'success',
          );
        } catch (e: any) {
          const msg = e?.response?.data?.detail ?? e?.message ?? 'unknown';
          showToast(`Mock squad failed: ${msg}`, 'error');
        }
      },
    },
    // Rev 3 / Product M3 · real-path discovery chip
    {
      kind: 'exec',
      id: 'TS-08',
      label: '🪖 Enable real squad (N=2)',
      descriptor:
        'Real LLM 2-member squad (~$0.20). Click to opt-in to auto_fire_squads, '
        + 'then type a prompt like: "Pick 2 personas from the roster to weigh in on '
        + 'this. Use request_squad." See docs/squad-real-path-smoke.md.',
      run: async ({ sessionId, showToast }) => {
        if (!sessionId) {
          showToast('No session — start a chat first.', 'error');
          return;
        }
        try {
          const res = await fetch(
            `/api/sessions/${sessionId}/autonomy/enable-squads`,
            { method: 'POST', credentials: 'include' },
          );
          if (!res.ok) {
            throw new Error(`status ${res.status}`);
          }
          showToast(
            'Auto-summon enabled · type a squad-summoning prompt next',
            'success',
          );
        } catch (e: any) {
          const msg = e?.message ?? 'unknown';
          showToast(`Enable failed: ${msg}`, 'error');
        }
      },
    },
  ],
};

// 2026-05-18 — Cookbook bench: numbered primitives that compose into named
// scenarios for rapid debugging of chatmap topology features. Run in order
// or pick-and-mix for branching.
//
// Topology vocabulary references docs/diagrams/VISUAL-LANGUAGE.md §2-§5.
//
// Reference scenarios (chip order → resulting topology):
//   Alpha · Solo→Squad linear:    CB-01 → CB-02 → CB-03 → CB-07
//                                 (user/asst → tool-fan → peer-fan → squad chrome)
//   Beta · Fork stack:            CB-01 → CB-04 → CB-05 → CB-06
//                                 (baseline → 1 fork → 3 forks total → approve all
//                                 → N=3 spine_fan_chip auto-collapse)
//   Gamma · Multi-spine:          CB-01 → CB-03 → CB-07
//                                 (baseline → consult_peers fan → squad spawn
//                                 → multiple spine_root_markers via squad members)
//   Delta · Mid-flight cleanup:   any → CB-08 (wipe pending proposals; spine intact)
//   Epsilon · Reset:              new chat (Trailhead "+ New" button)
const COOKBOOK_BENCH: ComposeTab = {
  id: 'cookbook',
  emoji: '🍳',
  label: 'Cookbook',
  descriptor:
    'Numbered primitives · α/β/γ scenarios for tool fans, fork stacks, '
    + 'multi-spine squads. Run in order or pick subsets. '
    + 'See docs/diagrams/png/cookbook-*.png for expected ChatMap topology.',
  actions: [
    {
      kind: 'insert',
      id: 'CB-01',
      label: '🌱 Plant',
      descriptor:
        '①  Baseline turn (user + asst tiles). Seeds the trail; required before all others.',
      prompt: 'Say hi back. No tools, just a short greeting.',
    },
    {
      kind: 'insert',
      id: 'CB-02',
      label: '🛠 Tool fan',
      descriptor:
        '②  Multi-tool round (lateral tool_call fan right of llm_round). Exercises tool subtree clearance.',
      prompt:
        'Look up the active persona roster, then search the knowledge base '
        + 'for "ChatDAGRAGDAG". Report what you find from each tool.',
    },
    {
      kind: 'insert',
      id: 'CB-03',
      label: '👥 Peer fan',
      descriptor:
        '③  consult_peers N=3 (peer fan as polar arc, fan_collapsed_chip when N≥3).',
      prompt:
        'Pull 3 peers from the storytelling pack and have them weigh in on '
        + 'whether a cold-open works for a 90-minute documentary about labor '
        + 'organizing. Substantive disagreement encouraged.',
    },
    {
      kind: 'exec',
      id: 'CB-04',
      label: '🌿 Fork (1)',
      descriptor:
        '④  Inject 1 propose_fork (queues a pending proposal). Approve via CB-06 or the Hill Climbers chip dialog.',
      run: async ({ sessionId, showToast }) => {
        if (!sessionId) {
          showToast('No session — start a chat first.', 'error');
          return;
        }
        const out = await councilLabApiClient.injectProposal(sessionId, {});
        notifyProposalsChanged(sessionId);
        showToast(`Fork queued (proposal ${out.id?.slice(0, 8) ?? '?'})`, 'success');
      },
    },
    {
      kind: 'exec',
      id: 'CB-05',
      label: '🌳 Multi-fork (+2)',
      descriptor:
        '⑤  Inject 2 more propose_forks (cumulative → N≥3 triggers spine_fan_chip auto-collapse after approve).',
      run: async ({ sessionId, showToast }) => {
        if (!sessionId) {
          showToast('No session — start a chat first.', 'error');
          return;
        }
        const out = await councilLabApiClient.injectProposals(sessionId, 2);
        notifyProposalsChanged(sessionId);
        showToast(`Queued ${out.count} more forks`, 'success');
      },
    },
    {
      kind: 'exec',
      id: 'CB-06',
      label: '✓ Approve all',
      descriptor:
        '⑥  Approve every pending proposal in one shot (materializes peer_response tiles via TURN_ADDED). Use after CB-04/05.',
      run: async ({ sessionId, showToast }) => {
        if (!sessionId) {
          showToast('No session — start a chat first.', 'error');
          return;
        }
        const { proposals } = await councilApiClient.listProposals(
          sessionId, 'pending',
        );
        if (proposals.length === 0) {
          showToast('No pending proposals to approve.', 'info');
          return;
        }
        let approved = 0;
        let failed = 0;
        for (const p of proposals) {
          try {
            await councilApiClient.resolveProposal(
              sessionId, p.id, { decision: 'approve' },
            );
            approved += 1;
          } catch {
            failed += 1;
          }
        }
        notifyProposalsChanged(sessionId);
        showToast(
          `Approved ${approved}/${proposals.length}` +
            (failed > 0 ? ` (${failed} failed/raced)` : ''),
          failed > 0 ? 'info' : 'success',
        );
      },
    },
    {
      kind: 'exec',
      id: 'CB-07',
      label: '🪖 Squad (N=3, mock)',
      descriptor:
        '⑦  Fire a mock squad with 3 synthetic peers ($0). Exercises squad chrome: dispatched banner → member tiles → synth turn with citation pills.',
      run: async ({ sessionId, showToast }) => {
        if (!sessionId) {
          showToast('No session — start a chat first.', 'error');
          return;
        }
        try {
          const out = await councilLabApiClient.fireSquadMock(sessionId, 3);
          showToast(
            `Mock squad dispatched (${out.member_count} members, mock_run=true)`,
            'success',
          );
        } catch (e: any) {
          const msg = e?.response?.data?.detail ?? e?.message ?? 'unknown';
          showToast(`Mock squad failed: ${msg}`, 'error');
        }
      },
    },
    {
      kind: 'exec',
      id: 'CB-08',
      label: '🧹 Wipe pending',
      descriptor:
        '⑧  Hard-delete all PENDING proposals (resolved rows untouched; spine unchanged). Cleanup between Beta runs.',
      run: async ({ sessionId, showToast }) => {
        if (!sessionId) {
          showToast('No session — start a chat first.', 'error');
          return;
        }
        const out = await councilLabApiClient.wipeProposals(sessionId);
        notifyProposalsChanged(sessionId);
        showToast(`Wiped ${out.wiped} pending proposal(s)`, 'success');
      },
    },
  ],
};


export const LAB_BENCHES: ComposeTab[] = [
  ...LAB_BENCH_DATA.map(benchToTab),
  HILL_CLIMBERS_BENCH,
  TRAIL_SMOKE_BENCH,
  COOKBOOK_BENCH,
];

// The Lab tab itself — empty actions array; content rendered by LabTabContent
export const labTab: ComposeTab = {
  id: 'lab',
  emoji: '🚧',
  label: 'Lab',
  descriptor: '9 benches · 56 experiments — pick a bench, fire a probe',
  actions: [], // nested rendering handles bench tabs
};

interface LabTabContentProps {
  activeLabBenchId: string;
  setActiveLabBenchId: (id: string) => void;
  onActionClick: (action: ComposeAction) => void;
  darkMode: boolean;
}

/**
 * LabTabContent — renders the 7-bench nested tab strip + active-bench
 * grid. Used inside ComposePanelTabContent when the top-level Lab tab
 * is active. Reuses the same h-[24rem] + content-start + auto-rows-min
 * grid pattern (Lab v2 layout-stability fix from journal 20260506-018).
 */
export const LabTabContent: React.FC<LabTabContentProps> = ({
  activeLabBenchId,
  setActiveLabBenchId,
  onActionClick,
  darkMode,
}) => {
  const activeBench = LAB_BENCHES.find((b) => b.id === activeLabBenchId) ?? LAB_BENCHES[0];

  return (
    <div className="flex flex-col">
      {/* Nested bench tab strip — flex-wrap pattern from Lab v2 */}
      <div
        role="tablist"
        aria-label="Lab benches"
        data-testid="lab-bench-strip"
        className={`flex flex-wrap gap-0.5 px-1 pt-1 flex-shrink-0 ${
          darkMode ? 'bg-gray-900' : 'bg-amber-50'
        }`}
      >
        {LAB_BENCHES.map((bench) => {
          const isActive = bench.id === activeLabBenchId;
          return (
            <button
              key={bench.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`lab-bench-${bench.id}`}
              onClick={() => setActiveLabBenchId(bench.id)}
              title={bench.descriptor}
              className={`flex-shrink-0 min-w-[5.5rem] px-2 py-1 rounded-t text-[10.5px] font-semibold transition-colors whitespace-nowrap ${
                isActive
                  ? darkMode
                    ? 'bg-gray-900/95 text-amber-300 border-b-2 border-amber-400'
                    : 'bg-white/80 text-amber-900 border-b-2 border-amber-500'
                  : darkMode
                    ? 'text-amber-500/70 hover:text-amber-300 hover:bg-amber-900/20'
                    : 'text-amber-700/70 hover:text-amber-900 hover:bg-amber-100/60'
              }`}
            >
              <span className="mr-1" aria-hidden>{bench.emoji}</span>
              {bench.label}
              <span className="ml-1 text-[9px] font-mono opacity-60">{bench.actions.length}</span>
            </button>
          );
        })}
      </div>

      {/* Bench descriptor */}
      <div
        className={`px-3 py-1.5 text-[10px] italic border-b ${
          darkMode
            ? 'bg-gray-900/95 text-amber-300/70 border-amber-500/20'
            : 'bg-white/80 text-amber-700/80 border-amber-200'
        }`}
      >
        <span className="mr-1" aria-hidden>{activeBench.emoji}</span>
        <span className="font-semibold">{activeBench.label}</span>
        <span className="mx-1 opacity-50">·</span>
        <span>{activeBench.descriptor}</span>
      </div>

      {/* Active bench's templates — 2-col grid, fixed-height + scroll */}
      <div
        role="tabpanel"
        data-testid={`lab-bench-panel-${activeBench.id}`}
        className={`grid grid-cols-2 gap-1 p-2 h-[24rem] overflow-y-auto content-start auto-rows-min scrollbar-thin ${
          darkMode
            ? 'bg-gray-900/95 scrollbar-thumb-amber-700/40 scrollbar-track-gray-900/40'
            : 'bg-white/70 scrollbar-thumb-amber-300 scrollbar-track-amber-50'
        }`}
      >
        {activeBench.actions.map((action) => (
          <button
            key={action.id}
            type="button"
            data-testid={`lab-template-${action.id}`}
            onClick={() => onActionClick(action)}
            className={`group flex flex-col items-start gap-0.5 px-2 py-1.5 rounded text-left transition-colors border min-h-[3.25rem] ${
              darkMode
                ? 'border-transparent hover:border-amber-500/40 hover:bg-amber-900/30'
                : 'border-transparent hover:border-amber-300 hover:bg-amber-100'
            }`}
          >
            <span className={`text-[11px] font-semibold leading-tight ${
              darkMode
                ? 'text-gray-100 group-hover:text-amber-200'
                : 'text-gray-900 group-hover:text-amber-900'
            }`}>
              {action.label}
            </span>
            {action.descriptor && (
              <span className={`text-[9.5px] leading-snug line-clamp-2 ${
                darkMode
                  ? 'text-gray-400 group-hover:text-amber-300/80'
                  : 'text-gray-500 group-hover:text-amber-700/90'
              }`}>
                {action.descriptor}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
